import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap, tap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import {
  ProjectTemplateFolderId,
  ProjectTemplateFolderResponse,
  ProjectTemplateFolderStructureResponse,
  SearchResult,
} from '@business/entities/project/ProjectTemplateFolderResponse';
import { ProjectTemplateFolderAPI } from '@business/api/project_template_folder_api';
import { BehaviorSubject, Observable } from 'rxjs';

export class ProjectTemplateFolderBloc extends BaseBloc {
  private readonly foldersRepo = new Repository<ProjectTemplateFolderResponse>({
    getItemId: folder => folder.id,
  });

  private readonly _breadcrumbs$ = new BehaviorSubject<{ id: string; name: string; object_type?: string }[]>([]);

  readonly folders$ = this.foldersRepo.items$;
  readonly selectFolder = this.foldersRepo.selectItem;
  readonly breadcrumbs$ = this._breadcrumbs$.asObservable();

  onReset() {
    this.foldersRepo.reset();
    this._breadcrumbs$.next([]);
  }

  /**
   * Fetch folders with optional parent filtering
   */
  fetchFolders = (parentId?: ProjectTemplateFolderId, objectType: string = 'project_template') => {
    return ProjectTemplateFolderAPI.fetchFolders(parentId, objectType).pipe(
      this.foldersRepo.ops.upsertMany(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_project_template_folder') }),
    );
  };

  /**
   * Fetch a specific folder by ID
   */
  fetchFolder = (id: ProjectTemplateFolderId) => {
    return ProjectTemplateFolderAPI.fetchFolder(id).pipe(
      tap(folder => {
        // Update breadcrumbs when fetching a folder
        this._breadcrumbs$.next(folder.breadcrumbs);
      }),
      this.foldersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_project_template_folder') }),
    );
  };

  /**
   * Create a new folder
   */
  createFolder = (data: Partial<ProjectTemplateFolderResponse> & { objectType?: string }) => {
    const folderData = {
      ...data,
      objectType: data.objectType || 'project_template',
    };

    return ProjectTemplateFolderAPI.createFolder(folderData).pipe(
      this.foldersRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_project_template_folder'),
      }),
    );
  };

  /**
   * Update an existing folder
   */
  updateFolder = (id: ProjectTemplateFolderId, data: Partial<ProjectTemplateFolderResponse>) => {
    return ProjectTemplateFolderAPI.updateFolder(id, data).pipe(
      this.foldersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_project_template_folder'),
      }),
    );
  };

  /**
   * Delete a folder with confirmation
   */
  deleteFolder = (id: ProjectTemplateFolderId, moveToParent: boolean = false) => {
    const confirmationMessage = moveToParent
      ? 'Do you really want to remove this folder? Items will be moved to the parent folder.'
      : 'Do you really want to remove this folder? All contents will be deleted.';

    return showDeleteConfirmation('Delete Folder', confirmationMessage).pipe(
      switchMap(() => ProjectTemplateFolderAPI.deleteFolder(id, moveToParent)),
      this.foldersRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_project_template_folder'),
      }),
    );
  };

  /**
   * Get folder structure for organizing
   */
  getFolderStructure = (
    folderId?: ProjectTemplateFolderId,
    objectType: string = 'project_template',
  ): Observable<ProjectTemplateFolderStructureResponse> => {
    return ProjectTemplateFolderAPI.folderStructure(folderId, objectType).pipe(handleMessage({ error: requestMessage('fetch_folder_structure') }));
  };

  /**
   * Get breadcrumbs for a folder
   */
  getBreadcrumbs = (id: ProjectTemplateFolderId) => {
    return ProjectTemplateFolderAPI.getBreadcrumbs(id).pipe(
      tap(response => {
        this._breadcrumbs$.next(response.breadcrumbs);
      }),
      handleMessage({ error: requestMessage('fetch_breadcrumbs') }),
    );
  };

  /**
   * Move a template to a different folder
   */
  moveTemplate = (templateId: string, templateType: string, toFolderId: string) => {
    return ProjectTemplateFolderAPI.moveTemplate({
      templateId,
      templateType,
      toFolderId,
    }).pipe(
      handleMessage({
        type: requestMessage('move_template'),
      }),
    );
  };

  /**
   * Move a folder to a different parent
   */
  moveFolder = (folderId: string, targetParentId: string) => {
    return this.updateFolder(folderId, { parentId: targetParentId }).pipe(
      // Refresh the folders list after moving
      switchMap(() => this.fetchFolders(targetParentId)),
      handleMessage({
        type: requestMessage('move_folder'),
      }),
    );
  };

  /**
   * Search templates and folders
   */
  searchTemplates = (query: string, objectType: string = 'project_template'): Observable<SearchResult[]> => {
    return ProjectTemplateFolderAPI.searchTemplates(query, objectType).pipe(handleMessage({ error: requestMessage('search_templates') }));
  };
}
